import React, { useEffect, useMemo, useCallback, useState } from 'react';
import { Box, Flex, TextV2, ButtonV2, TooltipV2, IconV2, DialogV2 } from '@withjoy/joykit';
import { ReactComponent as Info } from '@assets/icons/info.svg';
import { ReactComponent as Warning } from '@assets/icons/Error-32.svg';
import { CheckOutline } from '@withjoy/joykit/icons';
import { useTranslation } from '@shared/core';
import { useFeatureValue } from '@shared/core/featureFlags';
import { useCurrencyFormatter } from '@shared/utils/currency';
import { useWindowFunction } from '@shared/hooks/useWindowFunction';
import { pxToRem } from '@withjoy/joykit/theme';
import { DEFAULT_CURRENCY } from '@shared/utils/currency/constants';
import {
  transferDialogOverrides,
  TransferDialogBody,
  AllyPromoHeadline,
  AllyPromoSubtext,
  AllyAmountDisplay,
  AllyAmountSubtext,
  AllyNudgeBar,
  AllyNudgeText,
  AllyPrimaryButton,
  AllySecondaryButton,
  AllyFinePrint,
} from './TransferToBankDialog.styles';
import { TRANSFER_FEE, ALLY_PURPLE } from './constants';
import type { TransferToBankDialogProps, PayoutStatus } from './TransferToBankDialog.types';

const ALLY_URL = 'https://www.ally.com/';
// Update tq copy strings referencing "$200" if this value changes.
const ALLY_BONUS = 200;

type AllyTransferState = 'ally' | 'existing';

type AllyTransferPromoProps = Readonly<{
  allyAmountFormatted: string;
  standardAmountFormatted: string;
  feeFormatted: string;
  onConnectBank?: () => void;
  onBankPromoInteracted?: (cta: string, partner: string) => void;
}>;

const AllyTransferPromo: React.FC<AllyTransferPromoProps> = ({ allyAmountFormatted, standardAmountFormatted, feeFormatted, onConnectBank, onBankPromoInteracted }) => {
  const { tq } = useTranslation('transferToBankDialog');
  const openWindow = useWindowFunction('open');
  const [chosen, setChosen] = useState<AllyTransferState>('ally');
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [allyOpened, setAllyOpened] = useState(false);

  // ally→standard when previewing existing; standard→ally when previewing claim bonus; always standard once ally tab opened
  const showingAlly = !allyOpened && ((chosen === 'ally' && !isPreviewing) || (chosen === 'existing' && isPreviewing));

  const handleOpenAccount = useCallback(() => {
    onBankPromoInteracted?.('open_account', 'ally');
    openWindow(ALLY_URL, '_blank', 'noopener,noreferrer');
    setAllyOpened(true);
  }, [onBankPromoInteracted, openWindow]);

  const handleChooseExisting = useCallback(() => {
    onBankPromoInteracted?.('choose_existing', 'ally');
    setChosen('existing');
    setIsPreviewing(false);
  }, [onBankPromoInteracted]);

  const handleClaimBonus = useCallback(() => {
    onBankPromoInteracted?.('claim_bonus', 'ally');
    openWindow(ALLY_URL, '_blank', 'noopener,noreferrer');
    setAllyOpened(true);
  }, [onBankPromoInteracted, openWindow]);

  const handleContinueWithExisting = useCallback(() => {
    onBankPromoInteracted?.('continue', 'ally');
    onConnectBank?.();
  }, [onBankPromoInteracted, onConnectBank]);

  const handleContinueAnyway = useCallback(() => {
    onBankPromoInteracted?.('continue_anyway', 'ally');
    onConnectBank?.();
  }, [onBankPromoInteracted, onConnectBank]);

  const handlePreview = useCallback(() => {
    setIsPreviewing(true);
  }, []);

  const handleResetPreview = useCallback(() => {
    setIsPreviewing(false);
  }, []);

  return (
    <Flex
      flexDirection="column"
      alignItems="center"
      textAlign="center"
      paddingX={7}
      paddingBottom={6}
    >
      {!allyOpened && (
        <>
          <AllyPromoHeadline marginBottom={3}>{tq`The right first step of your new life together.`}</AllyPromoHeadline>
          <AllyPromoSubtext marginBottom={8}>{tq`Joy will add an extra $200 when you open an account with Ally.`}</AllyPromoSubtext>
        </>
      )}

      <AllyAmountDisplay style={{ color: showingAlly ? ALLY_PURPLE : '#555' }}>{showingAlly ? allyAmountFormatted : standardAmountFormatted}</AllyAmountDisplay>
      <AllyAmountSubtext>{showingAlly ? tq`With Ally + $200 from Joy · ${feeFormatted} transfer fee` : tq`${feeFormatted} transfer fee`}</AllyAmountSubtext>

      {allyOpened ? (
        <AllyPrimaryButton
          type="button"
          size="lg"
          shape="rounded"
          backgroundColor={ALLY_PURPLE}
          color="white"
          _hover={{ backgroundColor: '#5a2475' }}
          onClick={handleContinueWithExisting}
        >
          {tq`Continue`}
        </AllyPrimaryButton>
      ) : chosen === 'existing' ? (
        <AllyNudgeBar width="100%">
          <AllyNudgeText>{tq`Just a heads up — your $200 gift from Joy & Ally won't be applied to your existing account.`}</AllyNudgeText>
          <Flex gap={2}>
            <Box flex={1}>
              <ButtonV2
                type="button"
                fullWidth
                size="sm"
                shape="rounded"
                backgroundColor={ALLY_PURPLE}
                color="white"
                _hover={{ backgroundColor: '#5a2475' }}
                onMouseEnter={handlePreview}
                onMouseLeave={handleResetPreview}
                onClick={handleClaimBonus}
              >
                {tq`Claim my $200`}
              </ButtonV2>
            </Box>
            <Box flex={1}>
              <ButtonV2
                type="button"
                fullWidth
                size="sm"
                shape="rounded"
                variant="outline"
                intent="neutral"
                onClick={handleContinueAnyway}
              >
                {tq`Continue anyway`}
              </ButtonV2>
            </Box>
          </Flex>
        </AllyNudgeBar>
      ) : (
        <>
          <AllyPrimaryButton
            type="button"
            size="lg"
            shape="rounded"
            backgroundColor={ALLY_PURPLE}
            color="white"
            _hover={{ backgroundColor: '#5a2475' }}
            onClick={handleOpenAccount}
          >
            {tq`Get $200 more towards your new life`}
          </AllyPrimaryButton>
          <AllySecondaryButton
            type="button"
            size="lg"
            shape="rounded"
            variant="outline"
            intent="neutral"
            onMouseEnter={handlePreview}
            onMouseLeave={handleResetPreview}
            onClick={handleChooseExisting}
          >
            {tq`I'll just use my existing account`}
          </AllySecondaryButton>
        </>
      )}

      {!allyOpened && (
        <AllyFinePrint>
          {tq`$200 bonus requires a $500 min. deposit within 30 days. `}
          <TextV2
            as="a"
            href={ALLY_URL}
            target="_blank"
            rel="noopener noreferrer"
            typographyVariant="label2"
            style={{ color: '#bbb', textDecoration: 'underline' }}
            onClick={() => {
              onBankPromoInteracted?.('open_account', 'ally');
              setAllyOpened(true);
            }}
          >
            {tq`Terms apply.`}
          </TextV2>
        </AllyFinePrint>
      )}
    </Flex>
  );
};

type AllyRepeatPromoProps = Readonly<{
  allyAmountFormatted: string;
  standardAmountFormatted: string;
  feeFormatted: string;
  onAcknowledge: () => void;
  onBankPromoInteracted?: (cta: string, partner: string) => void;
}>;

const AllyRepeatPromo: React.FC<AllyRepeatPromoProps> = ({ allyAmountFormatted, standardAmountFormatted, feeFormatted, onAcknowledge, onBankPromoInteracted }) => {
  const { tq } = useTranslation('transferToBankDialog');
  const openWindow = useWindowFunction('open');
  const [chosen, setChosen] = useState<AllyTransferState>('ally');
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [allyOpened, setAllyOpened] = useState(false);

  const showingAlly = !allyOpened && ((chosen === 'ally' && !isPreviewing) || (chosen === 'existing' && isPreviewing));

  const handleOpenAccount = useCallback(() => {
    onBankPromoInteracted?.('open_account', 'ally');
    openWindow(ALLY_URL, '_blank', 'noopener,noreferrer');
    setAllyOpened(true);
  }, [onBankPromoInteracted, openWindow]);

  const handleChooseExisting = useCallback(() => {
    onBankPromoInteracted?.('choose_existing', 'ally');
    setChosen('existing');
    setIsPreviewing(false);
  }, [onBankPromoInteracted]);

  const handleClaimBonus = useCallback(() => {
    onBankPromoInteracted?.('claim_bonus', 'ally');
    openWindow(ALLY_URL, '_blank', 'noopener,noreferrer');
    setAllyOpened(true);
  }, [onBankPromoInteracted, openWindow]);

  const handleContinueAnyway = useCallback(() => {
    onBankPromoInteracted?.('continue_anyway', 'ally');
    onAcknowledge();
  }, [onBankPromoInteracted, onAcknowledge]);

  const handleContinue = useCallback(() => {
    onBankPromoInteracted?.('continue', 'ally');
    onAcknowledge();
  }, [onBankPromoInteracted, onAcknowledge]);

  const handlePreview = useCallback(() => setIsPreviewing(true), []);
  const handleResetPreview = useCallback(() => setIsPreviewing(false), []);

  return (
    <Flex
      flexDirection="column"
      alignItems="center"
      textAlign="center"
      paddingX={7}
      paddingBottom={6}
    >
      {!allyOpened && (
        <>
          <AllyPromoHeadline marginBottom={3}>{tq`The right first step of your new life together.`}</AllyPromoHeadline>
          <AllyPromoSubtext marginBottom={8}>{tq`Joy will add an extra $200 when you open an account with Ally.`}</AllyPromoSubtext>
        </>
      )}

      <AllyAmountDisplay style={{ color: showingAlly ? ALLY_PURPLE : '#555' }}>{showingAlly ? allyAmountFormatted : standardAmountFormatted}</AllyAmountDisplay>
      <AllyAmountSubtext>{showingAlly ? tq`With Ally + $200 from Joy · ${feeFormatted} transfer fee` : tq`${feeFormatted} transfer fee`}</AllyAmountSubtext>

      {allyOpened ? (
        <AllyPrimaryButton
          type="button"
          size="lg"
          shape="rounded"
          backgroundColor={ALLY_PURPLE}
          color="white"
          _hover={{ backgroundColor: '#5a2475' }}
          onClick={handleContinue}
        >
          {tq`Continue`}
        </AllyPrimaryButton>
      ) : chosen === 'existing' ? (
        <AllyNudgeBar width="100%">
          <AllyNudgeText>{tq`Just a heads up — your $200 gift from Joy & Ally won't be applied to your existing account.`}</AllyNudgeText>
          <Flex gap={2}>
            <Box flex={1}>
              <ButtonV2
                type="button"
                fullWidth
                size="sm"
                shape="rounded"
                backgroundColor={ALLY_PURPLE}
                color="white"
                _hover={{ backgroundColor: '#5a2475' }}
                onMouseEnter={handlePreview}
                onMouseLeave={handleResetPreview}
                onClick={handleClaimBonus}
              >
                {tq`Claim my $200`}
              </ButtonV2>
            </Box>
            <Box flex={1}>
              <ButtonV2
                type="button"
                fullWidth
                size="sm"
                shape="rounded"
                variant="outline"
                intent="neutral"
                onClick={handleContinueAnyway}
              >
                {tq`Continue anyway`}
              </ButtonV2>
            </Box>
          </Flex>
        </AllyNudgeBar>
      ) : (
        <>
          <AllyPrimaryButton
            type="button"
            size="lg"
            shape="rounded"
            backgroundColor={ALLY_PURPLE}
            color="white"
            _hover={{ backgroundColor: '#5a2475' }}
            onClick={handleOpenAccount}
          >
            {tq`Open an Ally account`}
          </AllyPrimaryButton>
          <AllySecondaryButton
            type="button"
            size="lg"
            shape="rounded"
            variant="outline"
            intent="neutral"
            onMouseEnter={handlePreview}
            onMouseLeave={handleResetPreview}
            onClick={handleChooseExisting}
          >
            {tq`Continue with transfer`}
          </AllySecondaryButton>
        </>
      )}

      {!allyOpened && (
        <AllyFinePrint>
          {tq`$200 bonus requires a $500 min. deposit within 30 days. `}
          <TextV2
            as="a"
            href={ALLY_URL}
            target="_blank"
            rel="noopener noreferrer"
            typographyVariant="label2"
            style={{ color: '#bbb', textDecoration: 'underline' }}
            onClick={() => {
              onBankPromoInteracted?.('open_account', 'ally');
              setAllyOpened(true);
            }}
          >
            {tq`Terms apply.`}
          </TextV2>
        </AllyFinePrint>
      )}
    </Flex>
  );
};

const StatusBody: React.FC<{
  status: PayoutStatus;
  onClose: () => void;
  onStatusCloseClicked?: (status: PayoutStatus) => void;
}> = ({ status, onClose, onStatusCloseClicked }) => {
  const { tq } = useTranslation('transferToBankDialog');

  const handleClose = useCallback(() => {
    onStatusCloseClicked?.(status);
    onClose();
  }, [status, onClose, onStatusCloseClicked]);

  return (
    <>
      <Flex
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        marginBottom={5}
      >
        <IconV2
          size="xxl"
          color={status === 'success' ? 'mono14' : 'negative6'}
        >
          {status === 'success' ? <CheckOutline /> : <Warning />}
        </IconV2>
        <TextV2
          textAlign="center"
          typographyVariant="display1"
          color="mono14"
        >
          {status === 'success' ? tq`Your Transfer is Being Proccessed` : tq`Oops! Something Went Wrong.`}
        </TextV2>
      </Flex>
      <Flex
        alignItems="center"
        justifyContent="center"
        flexDirection="column"
      >
        <Flex
          justifyContent="center"
          marginBottom={8}
        >
          <TextV2
            textAlign="center"
            typographyVariant="body3"
            color="mono12"
          >
            {status === 'success'
              ? tq`Woo-hoo! We're working on moving your cash fund balance to your bank account now. Please note that transferred funds typically arrive within 5 to 7 business days.`
              : tq`We're unable to complete this transfer. Please ensure the destination account is valid and try again. If this error persists, contact Joy Customer Support.`}
          </TextV2>
        </Flex>
        <Box>
          <ButtonV2
            onClick={handleClose}
            size="lg"
            intent="neutral"
            shape="rounded"
            variant="outline"
          >
            {tq`Close`}
          </ButtonV2>
        </Box>
      </Flex>
    </>
  );
};

export const TransferToBankDialogView: React.FC<TransferToBankDialogProps> = ({
  isOpen,
  onClose,
  availableBalance,
  currencyCode,
  onConnectBank,
  payoutFormProps,
  payoutStatus,
  telemetry,
}) => {
  const { tq } = useTranslation('transferToBankDialog');
  const { formatCurrency } = useCurrencyFormatter();
  const { value: activePartner } = useFeatureValue('activeCashFundPartner');
  const { value: transferPartner } = useFeatureValue('enableCashFundPartnerTransferToBank');
  const { value: repeatTransferPartner } = useFeatureValue('enableCashFundPartnerTransferToBankRepeat');
  const [allyRepeatAcknowledged, setAllyRepeatAcknowledged] = useState(false);

  const isBankNotConnected = !payoutFormProps;
  const showAllyTransferPromo = activePartner === 'ally' && transferPartner === 'ally' && isBankNotConnected;
  const isPendingView = !!payoutFormProps && payoutStatus === 'pending';
  const showAllyRepeatPromo =
    activePartner === 'ally' && repeatTransferPartner === 'ally' && isPendingView && !allyRepeatAcknowledged && (payoutFormProps?.isPayoutAllowed ?? false);
  const showConnectBankView = isBankNotConnected;
  const showDialogTitle = showConnectBankView || isPendingView || showAllyTransferPromo;
  const showStatusView = !!payoutFormProps && !!payoutStatus && payoutStatus !== 'pending';

  useEffect(() => {
    if (isOpen && showConnectBankView) {
      telemetry?.onConnectBankViewViewed?.();
    }
  }, [isOpen, showConnectBankView, telemetry]);

  useEffect(() => {
    if (isOpen && isPendingView) {
      telemetry?.onPendingViewViewed?.();
    }
  }, [isOpen, isPendingView, telemetry]);

  useEffect(() => {
    if (!isOpen) {
      setAllyRepeatAcknowledged(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && showAllyTransferPromo) {
      telemetry?.onBankPromoViewed?.();
    }
  }, [isOpen, showAllyTransferPromo, telemetry]);

  useEffect(() => {
    if (isOpen && showAllyRepeatPromo) {
      telemetry?.onBankPromoViewed?.();
    }
  }, [isOpen, showAllyRepeatPromo, telemetry]);

  const { formattedBalance, feeFormatted, totalFormatted, allyAmountFormatted } = useMemo(() => {
    const balance = availableBalance?.valueInMajorUnits ?? 0;
    const totalTransferValue = availableBalance?.valueInMajorUnits ? availableBalance.valueInMajorUnits - TRANSFER_FEE : 0;
    const allyTransferValue = totalTransferValue + ALLY_BONUS;
    const priceCurrencyCode = availableBalance?.currencyCode ?? currencyCode;
    return {
      formattedBalance: formatCurrency({
        priceFloatingPointDecimalString: balance.toString(),
        priceCurrencyCode,
        formatForm: 'auto',
      }),
      feeFormatted: formatCurrency({
        priceFloatingPointDecimalString: TRANSFER_FEE.toString(),
        priceCurrencyCode,
        formatForm: 'auto',
      }),
      totalFormatted: formatCurrency({
        priceFloatingPointDecimalString: totalTransferValue.toString(),
        priceCurrencyCode,
        formatForm: 'auto',
      }),
      allyAmountFormatted: formatCurrency({
        priceFloatingPointDecimalString: allyTransferValue.toString(),
        priceCurrencyCode,
        formatForm: 'auto',
      }),
    };
  }, [formatCurrency, availableBalance, currencyCode]);

  const renderBalanceAndFeeBlock = () => (
    <>
      <Flex
        flexDirection="column"
        alignItems="center"
        gap={1}
        paddingBottom={6}
      >
        <TextV2
          typographyVariant="display2"
          color="mono14"
        >
          {formattedBalance}
        </TextV2>
        <TextV2
          typographyVariant="hed1"
          color="mono14"
        >
          {tq`Transferrable Balance`}
        </TextV2>
      </Flex>

      <Flex
        flexDirection="column"
        gap={5}
        width="100%"
        marginBottom={8}
      >
        <Flex
          justifyContent="space-between"
          alignItems="center"
        >
          <Flex
            alignItems="center"
            gap={1}
          >
            <TextV2
              typographyVariant="body1"
              color="mono14"
            >
              {tq`Transfer Fee`}
            </TextV2>
            <TooltipV2
              placement="bottom"
              content={<TextV2 typographyVariant="label2">{tq`Transfer fee amount`}</TextV2>}
            >
              <IconV2
                size="sm"
                color="mono10"
              >
                <Info />
              </IconV2>
            </TooltipV2>
          </Flex>
          <TextV2
            typographyVariant="body1"
            color="mono14"
          >
            {feeFormatted}
          </TextV2>
        </Flex>
        <Flex
          justifyContent="space-between"
          alignItems="center"
        >
          <Flex
            alignItems="center"
            gap={1}
          >
            <TextV2
              typographyVariant="body1"
              color="mono14"
            >
              {tq`Total Transfer`}
            </TextV2>
            <TooltipV2
              placement="bottom"
              content={<TextV2 typographyVariant="label2">{tq`Transfer fee amount`}</TextV2>}
            >
              <IconV2
                size="sm"
                color="mono10"
              >
                <Info />
              </IconV2>
            </TooltipV2>
          </Flex>
          <TextV2
            typographyVariant="body1"
            color="mono14"
          >
            {totalFormatted}
          </TextV2>
        </Flex>
      </Flex>
    </>
  );

  return (
    <DialogV2
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      overrides={transferDialogOverrides}
    >
      <DialogV2.Header paddingY={6}>
        {showDialogTitle && (
          <TextV2
            typographyVariant="hed4"
            color="mono14"
            textAlign="center"
            paddingTop={6}
          >
            {tq`Transfer your funds`}
          </TextV2>
        )}
        <DialogV2.CloseButton />
      </DialogV2.Header>
      <DialogV2.Body overflow={[null, 'auto']}>
        {showStatusView && payoutStatus ? (
          <StatusBody
            status={payoutStatus}
            onClose={onClose}
            onStatusCloseClicked={telemetry?.onStatusCloseClicked}
          />
        ) : showAllyRepeatPromo ? (
          <AllyRepeatPromo
            allyAmountFormatted={allyAmountFormatted}
            standardAmountFormatted={totalFormatted}
            feeFormatted={feeFormatted}
            onAcknowledge={() => setAllyRepeatAcknowledged(true)}
            onBankPromoInteracted={telemetry?.onBankPromoInteracted}
          />
        ) : isPendingView && payoutFormProps ? (
          <TransferDialogBody
            flexDirection="column"
            textAlign="center"
          >
            <Flex
              justifyContent="center"
              marginBottom={5}
            >
              <TextV2
                textAlign="center"
                typographyVariant="body3"
                color="mono12"
              >
                {payoutFormProps.accountNumber
                  ? tq`You are about to transfer your cash balance to the account or card ending in ${payoutFormProps.accountNumber}. This transfer cannot be reversed.`
                  : tq`You are about to transfer your cash balance to your connected account or card. This transfer cannot be reversed.`}
              </TextV2>
            </Flex>
            {renderBalanceAndFeeBlock()}
            {!payoutFormProps.isPayoutAllowed && (
              <Flex
                alignItems="center"
                borderStyle="solid"
                borderWidth="1px"
                borderColor="negative3"
                borderRadius={3}
                backgroundColor="negative1"
                marginY={8}
                padding={5}
                width="100%"
              >
                <Box marginRight={6}>
                  <IconV2
                    size="lg"
                    color="negative5"
                  >
                    <Warning />
                  </IconV2>
                </Box>
                <Box>
                  <TextV2
                    typographyVariant="label2"
                    color="mono14"
                    textAlign="left"
                  >
                    {tq`Sorry! A minimum balance of $2 is needed to process a balance transfer.`}
                  </TextV2>
                </Box>
              </Flex>
            )}
            <Flex
              width="100%"
              justifyContent="center"
              gap={3}
              paddingBottom={6}
              flexDirection="column"
              alignItems="center"
              marginTop={8}
            >
              <ButtonV2
                size="lg"
                intent="neutral"
                shape="rounded"
                disabled={!payoutFormProps.isPayoutAllowed}
                loading={payoutFormProps.loadingPayoutRequest}
                onClick={() => {
                  const currentBalance = availableBalance?.valueInMajorUnits ?? 0;
                  telemetry?.onTransferBalanceClicked?.(currentBalance, availableBalance?.currencyCode ?? currencyCode ?? DEFAULT_CURRENCY, TRANSFER_FEE);
                  payoutFormProps.withdrawBalance();
                }}
                width={['100%', pxToRem(304)]}
                maxWidth="100%"
              >
                {tq`Yes, Transfer Balance`}
              </ButtonV2>
              <ButtonV2
                size="lg"
                intent="neutral"
                shape="rounded"
                variant="ghost"
                onClick={() => {
                  telemetry?.onNevermindClicked?.();
                  onClose();
                }}
                width={['100%', pxToRem(304)]}
                maxWidth="100%"
              >
                {tq`Nevermind`}
              </ButtonV2>
            </Flex>
          </TransferDialogBody>
        ) : showAllyTransferPromo ? (
          <AllyTransferPromo
            allyAmountFormatted={allyAmountFormatted}
            standardAmountFormatted={totalFormatted}
            feeFormatted={feeFormatted}
            onConnectBank={onConnectBank}
            onBankPromoInteracted={telemetry?.onBankPromoInteracted}
          />
        ) : (
          <TransferDialogBody
            flexDirection="column"
            textAlign="center"
          >
            {renderBalanceAndFeeBlock()}
            <Flex
              width="100%"
              justifyContent="center"
              gap={3}
              paddingBottom={6}
              flexDirection="column"
              alignItems="center"
              marginTop={8}
            >
              {onConnectBank && (
                <ButtonV2
                  size="lg"
                  shape="rounded"
                  onClick={onConnectBank}
                  width={['100%', pxToRem(304)]}
                  maxWidth="100%"
                  intent="neutral"
                >
                  {tq`Connect Bank Account`}
                </ButtonV2>
              )}
            </Flex>
          </TransferDialogBody>
        )}
      </DialogV2.Body>
    </DialogV2>
  );
};
